import React from "react";
import { ShieldCheck, Truck, Package, FileText } from "lucide-react";

const TermsAndConditions = () => {
  return (
    <div className="bg-gradient-to-tr from-yellow-100 via-white to-blue-100 min-h-screen py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto rounded-3xl bg-white/80 backdrop-blur-md shadow-2xl border border-gray-200 p-10">

        {/* Header */}
        <div className="mb-10 text-center">
          <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto shadow">
            <ShieldCheck className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-extrabold text-[#002a5c] mt-4">Terms & Conditions</h1>
          <p className="mt-2 text-gray-600 text-base">Last updated: June 26, 2025</p>
          <p className="mt-1 text-gray-500 text-sm">Please read these terms carefully before using our services.</p>
        </div>

        {/* Content */}
        <div className="space-y-10 text-gray-800 text-[16px] leading-relaxed">
          {[
            {
              title: "1. General Terms",
              icon: <Truck className="w-5 h-5" />,
              content: `By accessing or using the services of S Infinite Express Pvt. Ltd., you agree to comply with and be bound by these Terms & Conditions. These terms apply to all users of our logistics platform, whether they are shippers, consignees, or partners.`,
            },
            {
              title: "2. Shipping & Handling",
              icon: <Package className="w-5 h-5" />,
              content: (
                <ul className="list-disc ml-6 mt-2 space-y-1">
                  <li>All shipments are subject to availability and service coverage.</li>
                  <li>Shipping timelines provided are estimates and not guaranteed.</li>
                  <li>Customers must ensure accurate packaging, labeling, and documentation.</li>
                  <li>We reserve the right to refuse transportation of prohibited or hazardous items.</li>
                </ul>
              ),
            },
            {
              title: "3. Liability & Claims",
              icon: <FileText className="w-5 h-5" />,
              content: (
                <>
                  <p>
                    S Infinite Express is not liable for delays due to weather, customs, or other uncontrollable events. Claims for lost or damaged items must be reported within 7 days of delivery, along with supporting documents.
                  </p>
                  <p className="mt-2">
                    Our liability is limited to ₹5000 or the declared value, whichever is lower, unless additional insurance is purchased.
                  </p>
                </>
              ),
            },
            {
              title: "4. Data Privacy",
              icon: <ShieldCheck className="w-5 h-5" />,
              content: (
                <p>
                  We value your privacy. Your shipment and personal data are stored securely and used only to fulfill services or improve customer experience. We never sell or share data without consent. For more details, refer to our{" "}
                  <a href="/privacy-policy" className="text-blue-600 underline hover:text-blue-800 transition">
                    Privacy Policy
                  </a>
                  .
                </p>
              ),
            },
            {
              title: "5. Cancellation & Refund",
              icon: <Truck className="w-5 h-5" />,
              content: (
                <p>
                  Orders may be canceled before pickup. Once the shipment is in transit, cancellation is not allowed. Refunds, if applicable, are processed within 7–10 business days to the original payment method.
                </p>
              ),
            },
            {
              title: "6. Governing Law",
              icon: <FileText className="w-5 h-5" />,
              content: (
                <p>
                  These Terms shall be governed in accordance with Indian laws. Any disputes arising shall be subject to the jurisdiction of Mumbai, Maharashtra courts only.
                </p>
              ),
            },
            {
              title: "7. Contact Information",
              icon: <Package className="w-5 h-5" />,
              content: (
                <p>
                  For any queries or clarifications, contact our support at{" "}
                  <a href="mailto:support@sinfiniteexpress.com" className="text-blue-600 underline hover:text-blue-800">
                    support@sinfiniteexpress.com
                  </a>{" "}
                  or call +91-8237323531.
                </p>
              ),
            },
          ].map(({ title, icon, content }, idx) => (
            <section key={idx}>
              <h2 className="text-xl font-bold flex items-center text-[#002a5c] mb-2">
                <span className="bg-blue-100 text-blue-600 p-2 rounded-full mr-3 shadow">{icon}</span>
                {title}
              </h2>
              <div>{content}</div>
            </section>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-sm text-gray-500 border-t pt-6">
          © {new Date().getFullYear()} S Infinite Express Pvt. Ltd. All rights reserved.
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
